import React, { useEffect, useMemo, useState } from "react";

import { academyRequest, createCommandSender, createVersionedCommandSender } from "./academyApi";
import { checkIdentitySession } from "./identityClient";
import { COMMERCIAL_CATALOGUE_CHANGED } from "./useCommercialCatalogue";
import { useIdentity } from "./useIdentity";
import type {
  ApplicationCommercialOffering,
  ApplicationCommercialOfferingPage,
  CommercialLicense,
  CommercialLicensePage,
  CommercialOffering,
  CommercialOfferingDetail,
  CommercialOfferingPage,
  CommercialReceipt,
  FundingArrangement,
  FundingArrangementPage,
} from "./generated/commercial-catalogue";

type EntityKind = "organization" | "institution";
type Row = Record<string, string | number | boolean>;
type LicenseAction = "create" | "renew" | "submit";
type FundingAction = "create" | "submit";

const toInputTime = (value?: string | null) => {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.valueOf())
    ? ""
    : new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
const minor = (value: string) => (value === "" ? undefined : Math.round(Number(value) * 100));

async function allLicenses() {
  const items: CommercialLicense[] = [];
  let after: string | undefined;
  do {
    const page = await academyRequest<CommercialLicensePage>(
      `commercial-catalogue/licenses${after ? `?after=${encodeURIComponent(after)}` : ""}`,
    );
    items.push(...page.items);
    after = page.nextCursor;
  } while (after && items.length < 500);
  return items;
}

async function allFunding() {
  const items: FundingArrangement[] = [];
  let after: string | undefined;
  do {
    const page = await academyRequest<FundingArrangementPage>(
      `commercial-catalogue/funding${after ? `?after=${encodeURIComponent(after)}` : ""}`,
    );
    items.push(...page.items);
    after = page.nextCursor;
  } while (after && items.length < 500);
  return items;
}

async function ownedOfferingDetails() {
  const offerings: CommercialOffering[] = [];
  let after: string | undefined;
  do {
    const page = await academyRequest<CommercialOfferingPage>(
      `commercial-catalogue/offerings${after ? `?after=${encodeURIComponent(after)}` : ""}`,
    );
    offerings.push(...page.items);
    after = page.nextCursor;
  } while (after && offerings.length < 500);
  const details: CommercialOfferingDetail[] = [];
  for (let offset = 0; offset < offerings.length; offset += 6)
    details.push(
      ...(await Promise.all(
        offerings
          .slice(offset, offset + 6)
          .map(item =>
            academyRequest<CommercialOfferingDetail>(
              `commercial-catalogue/offerings/${encodeURIComponent(item.id)}`,
            ),
          ),
      )),
    );
  return details;
}

export function CommercialActionFields({
  entityKind,
  route,
  record,
  formId,
  onSaved,
  onBusy,
}: {
  entityKind: EntityKind;
  route: string;
  record?: Row;
  formId: string;
  onSaved: () => void;
  onBusy: (busy: boolean) => void;
}) {
  const account = useIdentity();
  const membership = account.access?.memberships.find(
    item => item.workspaceId === account.session?.workspace.id,
  );
  const entityId = entityKind === "organization" ? membership?.organizationId : membership?.institutionId;
  const kind = route === "scholarships-funding" ? "funding" : "licenses";
  const context = `${account.session?.tenantId || ""}:${account.session?.workspace.id || ""}:${entityId || ""}:${kind}`;
  const sendV2 = useMemo(() => createCommandSender({ headerOnly: true, context }), [context]);
  const sendV3 = useMemo(() => createVersionedCommandSender(context), [context]);
  const recordId = record ? String(record.id) : "";
  const [licenses, setLicenses] = useState<CommercialLicense[]>([]);
  const [funding, setFunding] = useState<FundingArrangement[]>([]);
  const [offerings, setOfferings] = useState<ApplicationCommercialOffering[]>([]);
  const [details, setDetails] = useState<CommercialOfferingDetail[]>([]);
  const [licenseAction, setLicenseAction] = useState<LicenseAction>(recordId ? "renew" : "create");
  const [fundingAction, setFundingAction] = useState<FundingAction>(recordId ? "submit" : "create");
  const [licenseId, setLicenseId] = useState(recordId);
  const [fundingId, setFundingId] = useState(recordId);
  const [offeringId, setOfferingId] = useState("");
  const [pricingVersionId, setPricingVersionId] = useState("");
  const [licenseKey, setLicenseKey] = useState("");
  const [capacity, setCapacity] = useState("");
  const [fundingType, setFundingType] = useState<FundingArrangement["fundingType"]>(
    entityKind === "institution" ? "institution_cohort" : "organization_seats",
  );
  const [name, setName] = useState("");
  const [eligibility, setEligibility] = useState("");
  const [allocation, setAllocation] = useState("");
  const [percentage, setPercentage] = useState("");
  const [maximumAward, setMaximumAward] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [validFrom, setValidFrom] = useState(toInputTime());
  const [validTo, setValidTo] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let current = true;
    void (async () => {
      setLoading(true);
      setError("");
      try {
        if (!entityId) throw new Error(`No current ${entityKind} membership is available in this workspace.`);
        const before = await checkIdentitySession();
        if (kind === "licenses") {
          const [offeringPage, rows] = await Promise.all([
            academyRequest<ApplicationCommercialOfferingPage>("commercial-catalogue/application-offerings"),
            allLicenses(),
          ]);
          if (!current) return;
          setOfferings(offeringPage.items);
          setLicenses(rows);
          const selected = rows.find(item => item.id === recordId) || rows[0];
          if (selected) {
            setLicenseId(selected.id);
            setOfferingId(selected.offeringId);
            setLicenseKey(selected.licenseKey);
            setCapacity(String(selected.capacity));
            setValidFrom(toInputTime(selected.validFrom));
            setValidTo(toInputTime(selected.validTo));
            if (recordId)
              setLicenseAction(["proposed", "rejected"].includes(selected.status) ? "submit" : "renew");
          } else setOfferingId(offeringPage.items[0]?.id || "");
        } else {
          const [arrangements, offeringDetails] = await Promise.all([allFunding(), ownedOfferingDetails()]);
          if (!current) return;
          setFunding(arrangements);
          setDetails(offeringDetails);
          const actionable = arrangements.filter(item => ["draft", "rejected"].includes(item.status));
          const selected = actionable.find(item => item.id === recordId) || actionable[0];
          setFundingId(selected?.id || "");
          const first = offeringDetails[0];
          setOfferingId(selected?.commercialOfferingId || first?.offering.id || "");
          setPricingVersionId(selected?.pricingVersionId || first?.prices[0]?.id || "");
        }
        const after = await checkIdentitySession();
        if (
          before.user.id !== after.user.id ||
          before.tenantId !== after.tenantId ||
          before.workspace.id !== after.workspace.id
        )
          throw new Error("Your account or workspace changed while commercial choices were loading.");
      } catch (cause) {
        if (current)
          setError(cause instanceof Error ? cause.message : "Could not load current commercial choices.");
      } finally {
        if (current) setLoading(false);
      }
    })();
    return () => {
      current = false;
    };
  }, [context, entityId, entityKind, kind, recordId]);

  useEffect(() => {
    if (kind !== "funding") return;
    const prices = details.find(item => item.offering.id === offeringId)?.prices || [];
    if (!prices.some(item => item.id === pricingVersionId)) setPricingVersionId(prices[0]?.id || "");
  }, [details, kind, offeringId, pricingVersionId]);

  const selectedLicense = licenses.find(item => item.id === licenseId);
  const selectedFunding = funding.find(item => item.id === fundingId);
  const selectedDetails = details.find(item => item.offering.id === offeringId);
  const pricing = selectedDetails?.prices || [];
  const valid =
    reason.trim().length >= 10 &&
    Boolean(entityId) &&
    !loading &&
    (kind === "licenses"
      ? licenseAction === "submit"
        ? Boolean(selectedLicense)
        : Boolean(offeringId && licenseKey.trim() && Number(capacity) > 0 && validFrom)
      : fundingAction === "submit"
        ? Boolean(selectedFunding && ["draft", "rejected"].includes(selectedFunding.status))
        : Boolean(pricingVersionId && name.trim() && validFrom));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || !entityId) return;
    onBusy(true);
    setError("");
    try {
      const before = await checkIdentitySession();
      if (kind === "licenses") {
        if (licenseAction === "create")
          await sendV2<CommercialReceipt>("commercial-catalogue/licenses/commands/create", {
            offeringId,
            licenseeType: entityKind,
            licenseeId: entityId,
            licenseKey: licenseKey.trim(),
            capacity: Number(capacity),
            validFrom: new Date(validFrom).toISOString(),
            ...(validTo ? { validTo: new Date(validTo).toISOString() } : {}),
            reason,
          });
        else if (licenseAction === "renew" && selectedLicense)
          await sendV2<CommercialReceipt>(
            `commercial-catalogue/licenses/${encodeURIComponent(selectedLicense.id)}/commands/renew`,
            {
              expectedVersion: selectedLicense.version,
              licenseKey: licenseKey.trim(),
              capacity: Number(capacity),
              validFrom: new Date(validFrom).toISOString(),
              ...(validTo ? { validTo: new Date(validTo).toISOString() } : {}),
              reason,
            },
          );
        else if (selectedLicense)
          await sendV2<CommercialReceipt>(
            `commercial-catalogue/licenses/${encodeURIComponent(selectedLicense.id)}/commands/submit`,
            { expectedVersion: selectedLicense.version, reason },
          );
      } else if (fundingAction === "create") {
        const price = pricing.find(item => item.id === pricingVersionId);
        if (!price) throw new Error("Choose an exact Pricing Version.");
        await sendV3<CommercialReceipt>(
          `v2/commercial-catalogue/programs/${encodeURIComponent(price.programId)}/funding/commands/create`,
          {
            programPriceId: price.id,
            fundingType,
            funderType: entityKind,
            funderId: entityId,
            name: name.trim(),
            ...(eligibility.trim() ? { eligibility: { description: eligibility.trim() } } : {}),
            ...(minor(allocation) === undefined ? {} : { allocationMinor: minor(allocation) }),
            ...(percentage === "" ? {} : { percentage: Number(percentage) }),
            ...(minor(maximumAward) === undefined ? {} : { maximumAwardMinor: minor(maximumAward) }),
            ...(allocation || maximumAward ? { currency } : {}),
            validFrom: new Date(validFrom).toISOString(),
            ...(validTo ? { validTo: new Date(validTo).toISOString() } : {}),
            reason,
          },
        );
      } else if (selectedFunding?.programId)
        await sendV3<CommercialReceipt>(
          `v2/commercial-catalogue/programs/${encodeURIComponent(selectedFunding.programId)}/funding/${encodeURIComponent(selectedFunding.id)}/commands/submit`,
          { reason },
          selectedFunding.version,
        );
      const after = await checkIdentitySession();
      if (
        before.user.id !== after.user.id ||
        before.tenantId !== after.tenantId ||
        before.workspace.id !== after.workspace.id
      )
        throw new Error("Your account or workspace changed while the commercial command was recorded.");
      window.dispatchEvent(new Event(COMMERCIAL_CATALOGUE_CHANGED));
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not record the commercial command.");
    } finally {
      onBusy(false);
    }
  };

  return (
    <form id={formId} className="form-fields-v2" onSubmit={submit}>
      {kind === "licenses" ? (
        <>
          <div className="form-field-v2 full">
            <label>Commercial action</label>
            <select
              aria-label="Commercial action"
              value={licenseAction}
              onChange={event => setLicenseAction(event.target.value as LicenseAction)}
            >
              <option value="create">Create license proposal</option>
              <option value="renew" disabled={!selectedLicense}>
                Renew exact License
              </option>
              <option
                value="submit"
                disabled={!selectedLicense || !["proposed", "rejected"].includes(selectedLicense.status)}
              >
                Submit exact License for review
              </option>
            </select>
          </div>
          {licenseAction !== "create" && (
            <div className="form-field-v2 full">
              <label>Exact Commercial License</label>
              <select
                aria-label="Exact Commercial License"
                required
                value={licenseId}
                onChange={event => {
                  const item = licenses.find(row => row.id === event.target.value);
                  setLicenseId(event.target.value);
                  if (item) {
                    setOfferingId(item.offeringId);
                    setLicenseKey(item.licenseKey);
                    setCapacity(String(item.capacity));
                    setValidFrom(toInputTime(item.validFrom));
                    setValidTo(toInputTime(item.validTo));
                  }
                }}
              >
                <option value="" disabled>
                  Choose a License…
                </option>
                {licenses.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.offeringName} · {item.licenseKey} · v{item.version} · {item.status}
                  </option>
                ))}
              </select>
            </div>
          )}
          {licenseAction !== "submit" && (
            <>
              <div className="form-field-v2 full">
                <label>Exact Product Offering</label>
                <select
                  aria-label="Exact Product Offering"
                  required
                  value={offeringId}
                  onChange={event => setOfferingId(event.target.value)}
                  disabled={licenseAction === "renew"}
                >
                  <option value="" disabled>
                    Choose an Offering…
                  </option>
                  {offerings.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.programName} · {item.name} · Program v{item.programVersion ?? "current"}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field-v2">
                <label>License reference</label>
                <input
                  aria-label="License reference"
                  required
                  maxLength={120}
                  value={licenseKey}
                  onChange={event => setLicenseKey(event.target.value)}
                />
              </div>
              <div className="form-field-v2">
                <label>Capacity</label>
                <input
                  aria-label="Capacity"
                  required
                  type="number"
                  min="1"
                  value={capacity}
                  onChange={event => setCapacity(event.target.value)}
                />
              </div>
              <div className="form-field-v2">
                <label>Valid from</label>
                <input
                  aria-label="Valid from"
                  required
                  type="datetime-local"
                  value={validFrom}
                  onChange={event => setValidFrom(event.target.value)}
                />
              </div>
              <div className="form-field-v2">
                <label>Valid until (optional)</label>
                <input
                  aria-label="Valid until (optional)"
                  type="datetime-local"
                  min={validFrom}
                  value={validTo}
                  onChange={event => setValidTo(event.target.value)}
                />
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className="form-field-v2 full">
            <label>Funding action</label>
            <select
              aria-label="Funding action"
              value={fundingAction}
              onChange={event => setFundingAction(event.target.value as FundingAction)}
            >
              <option value="create">Create Funding Arrangement</option>
              <option
                value="submit"
                disabled={!funding.some(item => ["draft", "rejected"].includes(item.status))}
              >
                Submit exact Funding Arrangement for review
              </option>
            </select>
          </div>
          {fundingAction === "submit" ? (
            <div className="form-field-v2 full">
              <label>Exact Funding Arrangement</label>
              <select
                aria-label="Exact Funding Arrangement"
                required
                value={fundingId}
                onChange={event => setFundingId(event.target.value)}
              >
                <option value="" disabled>
                  Choose a Funding Arrangement…
                </option>
                {funding
                  .filter(item => ["draft", "rejected"].includes(item.status))
                  .map(item => (
                    <option key={item.id} value={item.id}>
                      {item.name} · Funding v{item.fundingVersion} · record v{item.version}
                    </option>
                  ))}
              </select>
            </div>
          ) : (
            <>
              <div className="form-field-v2 full">
                <label>Exact Product Offering</label>
                <select
                  aria-label="Exact Product Offering"
                  required
                  value={offeringId}
                  onChange={event => setOfferingId(event.target.value)}
                >
                  <option value="" disabled>
                    Choose an Offering…
                  </option>
                  {details.map(item => (
                    <option key={item.offering.id} value={item.offering.id}>
                      {item.offering.name} · {item.offering.offeringKey}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field-v2 full">
                <label>Exact Pricing Version</label>
                <select
                  aria-label="Exact Pricing Version"
                  required
                  value={pricingVersionId}
                  onChange={event => setPricingVersionId(event.target.value)}
                >
                  <option value="" disabled>
                    Choose a Pricing Version…
                  </option>
                  {pricing.map(item => (
                    <option key={item.id} value={item.id}>
                      Price v{item.priceVersion} · {item.currency} · {item.status}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field-v2">
                <label>Funding type</label>
                <select
                  aria-label="Funding type"
                  value={fundingType}
                  onChange={event => setFundingType(event.target.value as FundingArrangement["fundingType"])}
                >
                  {[
                    "scholarship",
                    "sponsorship",
                    "organization_seats",
                    "institution_cohort",
                    "grant",
                    "discount",
                    "other",
                  ].map(item => (
                    <option key={item} value={item}>
                      {item.replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field-v2">
                <label>Authorized funder</label>
                <input
                  aria-label="Authorized funder"
                  readOnly
                  value={`${entityKind} · ${entityId || "unavailable"}`}
                />
              </div>
              <div className="form-field-v2 full">
                <label>Name</label>
                <input
                  aria-label="Name"
                  required
                  value={name}
                  onChange={event => setName(event.target.value)}
                />
              </div>
              <div className="form-field-v2">
                <label>Coverage percentage</label>
                <input
                  aria-label="Coverage percentage"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={percentage}
                  onChange={event => setPercentage(event.target.value)}
                />
              </div>
              <div className="form-field-v2">
                <label>Maximum award</label>
                <input
                  aria-label="Maximum award"
                  type="number"
                  min="0"
                  step="0.01"
                  value={maximumAward}
                  onChange={event => setMaximumAward(event.target.value)}
                />
              </div>
              <div className="form-field-v2">
                <label>Total allocation</label>
                <input
                  aria-label="Total allocation"
                  type="number"
                  min="0"
                  step="0.01"
                  value={allocation}
                  onChange={event => setAllocation(event.target.value)}
                />
              </div>
              <div className="form-field-v2">
                <label>Currency</label>
                <input
                  aria-label="Currency"
                  maxLength={3}
                  value={currency}
                  onChange={event => setCurrency(event.target.value.toUpperCase())}
                />
              </div>
              <div className="form-field-v2">
                <label>Valid from</label>
                <input
                  aria-label="Valid from"
                  required
                  type="datetime-local"
                  value={validFrom}
                  onChange={event => setValidFrom(event.target.value)}
                />
              </div>
              <div className="form-field-v2">
                <label>Valid until (optional)</label>
                <input
                  aria-label="Valid until (optional)"
                  type="datetime-local"
                  min={validFrom}
                  value={validTo}
                  onChange={event => setValidTo(event.target.value)}
                />
              </div>
              <div className="form-field-v2 full">
                <label>Eligibility</label>
                <textarea
                  aria-label="Eligibility"
                  value={eligibility}
                  onChange={event => setEligibility(event.target.value)}
                />
              </div>
            </>
          )}
        </>
      )}
      <div className="form-field-v2 full">
        <label>Attributable reason</label>
        <textarea
          aria-label="Attributable reason"
          required
          minLength={10}
          value={reason}
          onChange={event => setReason(event.target.value)}
        />
      </div>
      {loading && (
        <p className="full" role="status">
          Loading authorized commercial choices…
        </p>
      )}
      {error && (
        <p className="full" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}
